import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const HotPot: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Chinese Hot Pot Guide | Chinese Food Maps</title>
        <meta name="description" content="Learn how to prepare and enjoy Chinese hot pot at home. Our comprehensive guide covers broths, ingredients, dipping sauces, and hot pot etiquette." />
        <meta property="og:title" content="Chinese Hot Pot Guide | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to prepare and enjoy Chinese hot pot at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/hot-pot" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/hot-pot" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Chinese Hot Pot Guide</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Hot Pot (火锅) is a communal dining experience where diners cook fresh ingredients in a simmering pot of broth at the table. This guide will help you create an authentic hot pot experience at home.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Basic Broth Recipes:</h3>
            
            <h4 className="text-lg font-semibold text-red-700 mb-2">Sichuan Spicy Broth:</h4>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>4 cups chicken stock</li>
              <li>2 tbsp Sichuan peppercorns</li>
              <li>3 tbsp doubanjiang (spicy bean paste)</li>
              <li>4 cloves garlic, crushed</li>
              <li>2-inch ginger, sliced</li>
              <li>2 green onions</li>
              <li>2 dried chili peppers</li>
            </ul>

            <h4 className="text-lg font-semibold text-red-700 mb-2">Clear Broth:</h4>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>4 cups chicken stock</li>
              <li>2-inch ginger, sliced</li>
              <li>2 green onions</li>
              <li>2 cloves garlic</li>
              <li>Salt to taste</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Recommended Ingredients:</h3>
            
            <h4 className="text-lg font-semibold text-red-700 mb-2">Meat and Seafood:</h4>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Thinly sliced lamb</li>
              <li>Sliced beef</li>
              <li>Fish balls</li>
              <li>Shrimp</li>
              <li>Squid</li>
            </ul>

            <h4 className="text-lg font-semibold text-red-700 mb-2">Vegetables:</h4>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Napa cabbage</li>
              <li>Mushrooms (various types)</li>
              <li>Spinach</li>
              <li>Corn</li>
              <li>Bean sprouts</li>
            </ul>

            <h4 className="text-lg font-semibold text-red-700 mb-2">Other Items:</h4>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Tofu (firm and soft)</li>
              <li>Glass noodles</li>
              <li>Udon noodles</li>
              <li>Dumplings</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Dipping Sauce Guide:</h3>
            <p className="mb-4">Create your own sauce combination using:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Sesame sauce</li>
              <li>Soy sauce</li>
              <li>Chili oil</li>
              <li>Minced garlic</li>
              <li>Chopped cilantro</li>
              <li>Chopped green onions</li>
              <li>Sesame oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Hot Pot Etiquette:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>Wait for the broth to come to a boil before adding ingredients</li>
              <li>Use different utensils for raw and cooked foods</li>
              <li>Don't overcrowd the pot</li>
              <li>Cook meat and seafood thoroughly</li>
              <li>Add vegetables gradually to maintain broth temperature</li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Freeze meat slightly for easier slicing</li>
                <li>Arrange ingredients by cooking time</li>
                <li>Keep broth at a gentle simmer</li>
                <li>Prepare all ingredients before starting</li>
                <li>Have plenty of dipping sauce options</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};